import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["input", "results"];
  static values = { url: String, currentEventId: String };

  connect() {
    this.debounceTimer = null;
    this.boundKeydown = this.handleGlobalKeydown.bind(this);
    this.boundBackdropClick = this.handleBackdropClick.bind(this);
    this.boundReset = this.reset.bind(this);
    this.boundActivateFirst = this.activateFirstOnLoad.bind(this);
    this.boundFetchError = this.handleFetchError.bind(this);
    document.addEventListener("keydown", this.boundKeydown);
    this.element.addEventListener("click", this.boundBackdropClick);
    this.element.addEventListener("close", this.boundReset);
    this.element.addEventListener("turbo:frame-load", this.boundActivateFirst);
    this.element.addEventListener(
      "turbo:fetch-request-error",
      this.boundFetchError,
    );
  }

  disconnect() {
    document.removeEventListener("keydown", this.boundKeydown);
    this.element.removeEventListener("click", this.boundBackdropClick);
    this.element.removeEventListener("close", this.boundReset);
    this.element.removeEventListener(
      "turbo:frame-load",
      this.boundActivateFirst,
    );
    this.element.removeEventListener(
      "turbo:fetch-request-error",
      this.boundFetchError,
    );
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
  }

  handleGlobalKeydown(e) {
    if ((e.metaKey || e.ctrlKey) && e.key === "k") {
      e.preventDefault();
      if (this.element.open) {
        this.close();
      } else {
        this.open();
      }
    }
  }

  handleBackdropClick(e) {
    if (e.target === this.element) this.close();
  }

  open() {
    this.element.showModal();
    this.inputTarget.focus();
    this.inputTarget.select();
    this.inputTarget.setAttribute("aria-expanded", "true");
    this.performSearch();
  }

  close() {
    this.element.close();
  }

  reset() {
    this.inputTarget.value = "";
    this.inputTarget.setAttribute("aria-expanded", "false");
    this.updateActiveDescendant(null);
  }

  search() {
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => this.performSearch(), 200);
  }

  async performSearch() {
    const q = this.inputTarget.value.trim();
    const params = new URLSearchParams({ q });
    if (this.currentEventIdValue)
      params.set("except", this.currentEventIdValue);
    this.resultsTarget.src = `${this.urlValue}?${params}`;
  }

  handleFetchError(e) {
    e.preventDefault();
    this.resultsTarget.innerHTML =
      '<p class="spotlight-empty">Something went wrong. Please try again.</p>';
    this.inputTarget.setAttribute("aria-expanded", "false");
    this.updateActiveDescendant(null);
  }

  updateActiveDescendant(id) {
    if (id) {
      this.inputTarget.setAttribute("aria-activedescendant", id);
    } else {
      this.inputTarget.removeAttribute("aria-activedescendant");
    }
  }

  navigate(e) {
    if (e.key === "Escape") {
      this.close();
      return;
    }

    const items = this.resultItems();
    if (!items.length) return;

    const active = this.activeItem(items);

    if (e.key === "ArrowDown") {
      e.preventDefault();
      const next = active === null ? 0 : Math.min(active + 1, items.length - 1);
      this.activateItem(items, next);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      const next = active === null ? items.length - 1 : Math.max(active - 1, 0);
      this.activateItem(items, next);
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (active !== null) {
        items[active].querySelector("a, button").click();
      }
    }
  }

  activateFirstOnLoad() {
    const items = this.resultItems();
    if (items.length) {
      this.activateItem(items, 0);
    } else {
      this.updateActiveDescendant(null);
    }
    this.inputTarget.setAttribute(
      "aria-expanded",
      items.length ? "true" : "false",
    );
  }

  resultItems() {
    return Array.from(this.element.querySelectorAll(".spotlight-results li"));
  }

  activeItem(items) {
    const idx = items.findIndex(
      (li) => li.getAttribute("aria-selected") === "true",
    );
    return idx === -1 ? null : idx;
  }

  activateItem(items, idx) {
    items.forEach((li, i) => {
      li.setAttribute("aria-selected", i === idx ? "true" : "false");
    });
    items[idx]?.scrollIntoView({ block: "nearest" });
    this.updateActiveDescendant(items[idx]?.id || null);
  }
}
